/**
 * The staff-internal comment thread on a project, a data object, an analysis or a report (P16):
 * oldest first, the author may edit or delete their own, anyone one without an author. It is
 * the one place for internal notes; no client ever sees it.
 */
import { useState, type KeyboardEvent } from 'react';
import { Box, Button, Card, Group, Loader, Stack, Text, Textarea, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAddComment, useComments, useDeleteComment, useEditComment, type Comment } from '@/api/comments';
import { errorMessage } from '@/api/errors';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

const MAX_BODY = 4000;

/** Ctrl+Enter (Cmd+Enter on a Mac) sends, as in most chat boxes. */
const sendsOn = (submit: () => void) => (e: KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    submit();
  }
};

function Row({ comment, subject }: { comment: Comment; subject: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { relative, dateTime } = useFormat();
  const edit = useEditComment(subject);
  const remove = useDeleteComment(subject);
  const [draft, setDraft] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const fail = (err: unknown) => notifications.show({ color: 'red', message: errorMessage(err) });

  const save = () => {
    if (draft === null || !draft.trim()) return;
    edit.mutate({ id: comment.public_id, body: draft }, { onSuccess: () => setDraft(null), onError: fail });
  };

  return (
    <Box py={10} style={{ borderBottom: `1px solid ${brand.border}` }} data-testid="comment">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text fz={13} fw={600}>
          {comment.author ? comment.author.full_name || comment.author.email : t('comments.unknownAuthor')}
        </Text>
        <Text fz={12} c={brand.textMuted} title={dateTime(comment.created_at)} style={{ flexShrink: 0 }}>
          {relative(comment.created_at)}
          {comment.edited_at && <span title={dateTime(comment.edited_at)}> · {t('comments.edited')}</span>}
        </Text>
      </Group>
      {draft === null ? (
        <Text fz={14} mt={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {comment.body}
        </Text>
      ) : (
        <Textarea
          mt={6}
          autosize
          minRows={2}
          maxLength={MAX_BODY}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={sendsOn(save)}
          aria-label={t('comments.edit')}
          autoFocus
        />
      )}
      {comment.can_edit && (
        <Group gap={4} mt={6}>
          {draft === null ? (
            <>
              <Button size="compact-xs" variant="subtle" onClick={() => setDraft(comment.body)}>
                {t('comments.edit')}
              </Button>
              <Button
                size="compact-xs"
                color="red"
                variant={confirming ? 'light' : 'subtle'}
                loading={remove.isPending}
                onClick={() => (confirming ? remove.mutate(comment.public_id, { onError: fail }) : setConfirming(true))}
                onBlur={() => setConfirming(false)}
              >
                {confirming ? t('comments.confirmDelete') : t('comments.delete')}
              </Button>
            </>
          ) : (
            <>
              <Button size="compact-xs" onClick={save} loading={edit.isPending} disabled={!draft.trim()}>
                {tc('common.save')}
              </Button>
              <Button size="compact-xs" variant="subtle" onClick={() => setDraft(null)}>
                {tc('common.cancel')}
              </Button>
            </>
          )}
        </Group>
      )}
    </Box>
  );
}

export function InternalComments({ subject }: { subject: string }) {
  const { t } = useTranslation('projects');
  const comments = useComments(subject);
  const add = useAddComment(subject);
  const [body, setBody] = useState('');

  const submit = () => {
    if (!body.trim()) return;
    add.mutate(body, {
      onSuccess: () => setBody(''),
      onError: (err) => notifications.show({ color: 'red', message: errorMessage(err) }),
    });
  };

  return (
    <Card data-testid="internal-comments">
      <Title order={3}>{t('comments.title')}</Title>
      <Text fz={12} c={brand.textMuted} mb="xs">
        {t('comments.hint')}
      </Text>
      {comments.isPending ? (
        <Loader color="forest" size="sm" />
      ) : (comments.data ?? []).length === 0 ? (
        <Text fz={14} c={brand.textMuted}>
          {t('comments.empty')}
        </Text>
      ) : (
        <Stack gap={0}>
          {(comments.data ?? []).map((c) => (
            <Row key={c.public_id} comment={c} subject={subject} />
          ))}
        </Stack>
      )}
      <Textarea
        mt="md"
        autosize
        minRows={2}
        maxLength={MAX_BODY}
        placeholder={t('comments.placeholder')}
        aria-label={t('comments.add')}
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        onKeyDown={sendsOn(submit)}
      />
      <Group justify="flex-end" mt="xs">
        <Button size="xs" onClick={submit} loading={add.isPending} disabled={!body.trim()}>
          {t('comments.add')}
        </Button>
      </Group>
    </Card>
  );
}

/** A comment count beside a row's name in the project's staff lists; nothing when there is none. */
export function CommentCount({ count }: { count: number | undefined }) {
  const { t } = useTranslation('projects');
  if (!count) return null;
  const label = t('comments.count', { count });
  return (
    <Group component="span" gap={3} wrap="nowrap" title={label} aria-label={label} c={brand.textMuted} style={{ display: 'inline-flex', verticalAlign: 'middle' }} data-testid="comment-count">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <Text component="span" fz={12} c={brand.textMuted}>
        {count}
      </Text>
    </Group>
  );
}
